import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import TextareaAutosize from 'react-textarea-autosize';
import classNames from 'classnames';

import { OrderPaymentOptionsWithDebit } from '../components/payment/OrderPaymentOptionsWithDebit';
import { OrderScreen } from '../OrderScreen';
import { getPaymentMethod, PaymentInfo } from '../utils/useCartPayment';
import { CreditDebitCardForm, CreditCardFields } from '../components/payment/CreditDebitCardForm';

import {
    OrderScreenContext,
    CheckoutFlowState,
} from 'features/order-summary/screens/CheckoutScreen';

import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { Accordion } from 'features/product/components/Accordion';
import {
    PaymentType,
    CreditCardPaymentMethod,
    ACHPaymentMethod,
    CheckPaymentMethod,
    PaymentSubType,
    PaymentMethod,
} from 'common/api/e-comm/payment-types';
import { Routes } from 'routes';
import {
    getDefaultPaymentMethodThunk,
    setDefaultPaymentMethodThunk,
} from 'common/features/store/duck/account/duck';
import { RootState, useRootSelector } from 'rootStore';
import { addBillingInfo } from 'common/api/e-comm/orders/service';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { useAlert } from 'modules/alerts';
import { MAX_SHIP_TO_HOME_CARD_QUANTITY, SERVICE_UNAVAILABLE_MESSAGE } from 'config';
import {
    PayWithCreditCardRequest,
    PayWithDebitCardRequest,
    emptyDebitCard,
    emptyCreditCard,
} from 'common/types/CheckoutRequest';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { CartInfoState } from '../CartInfoState';
import { isOrderExceedsMaxCards } from 'features/cart/utils/cartCheck';
import { Formik, FormikHelpers } from 'formik';
import { Modal, ModalFooter, ModalPrimaryBtn, ModalSecondaryBtn } from 'components/modal/Modal';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { validateCardBinWithTokenX } from 'common/api/e-comm/credit-cards/service';
import { isInIframe, mainWebsiteOrigin } from 'utils';

const styles = createStyles({
    page: ['flex', { maxWidth: '580px' }],
    termsContent: [
        'w-full text-sm mt-1 text-grey-1 font-normal resize-none',
        { lineHeight: '22px' },
    ],
    btn: 'max-w-xs',
});

export type OrderPaymentScreenParams = {
    shipToHome: string | undefined;
};

interface ModalExceedsMaxCardsProps {
    maxCards: number;
    isOpen: boolean;
    closeModal: () => void;
    primaryButton: {
        text: string;
        action: () => void;
    };
    secondaryButton?: {
        text: string;
        action: () => void;
    };
    isShipToCoordinatorEnabled: boolean;
}

const ModalExceedsMaxCards = ({
    maxCards,
    isOpen,
    closeModal,
    primaryButton,
    secondaryButton,
    isShipToCoordinatorEnabled,
}: ModalExceedsMaxCardsProps) => {
    const textContent = useMemo(() => {
        return isShipToCoordinatorEnabled
            ? `Ship to Home orders have a maximum of ${maxCards} cards. The Physical Cards in your order
        will be sent to your coordinator. If you want to Ship to Home, you will need to
        place a separate order.`
            : `Ship to Home orders have a maximum of ${maxCards} cards. 
        In order to Ship to Home additional cards, you will need to place a separate order.`;
    }, [isShipToCoordinatorEnabled, maxCards]);

    return (
        <Modal title="" isOpen={isOpen} onClose={closeModal}>
            <h3 className=" font-semibold text-3xl text-center sm:px-10 px-2">{`${maxCards} Cards Max For Ship to Home Orders`}</h3>
            <div className="sm:-mb-4 mb-2 mt-8 sm:px-10 px-2 text-grey-medium font-normal text-center">
                {textContent}
            </div>
            <ModalFooter>
                <ModalPrimaryBtn onClick={primaryButton.action}>
                    {primaryButton.text}
                </ModalPrimaryBtn>
                {secondaryButton ? (
                    <ModalSecondaryBtn onClick={secondaryButton.action}>
                        {secondaryButton.text}
                    </ModalSecondaryBtn>
                ) : null}
            </ModalFooter>
        </Modal>
    );
};

const OrderPaymentScreenContent = () => {
    useScrollToTop();

    const { defaultPaymentAsyncState } = useRootSelector((s) => s.store.account);
    const dispatch = useDispatch<any>();
    const [makeThisTheDefaultPayment, setMakeThisTheDefaultPayment] = useState(false);
    const { cartInfo, setCartInfo, cartInfoDetails, checkoutFlowState, setCheckoutFlowState } =
        useContext(OrderScreenContext);
    const history = useHistory();
    const { paymentMethodId } = cartInfo;
    const { shipToHomeIfPossible } = cartInfoDetails;
    const { cart } = useRootSelector((s) => s.store.cart);
    const homeDetails = useHomeDetails();
    const disableCheckoutBtn = checkoutFlowState === CheckoutFlowState.DISABLED;
    const paymentTypes = useRootSelector((s) => s.store.organization.paymentTypes);
    const alert = useAlert();
    const isShipCoordinatorEnabled = useRootSelector(selectIsShipToCoordinatorEnabled);
    const [showExceededModal, setShowExceededModal] = useState(false);
    const { isEnabled } = useFeatureFlags();

    const onNewCreditCardConfirm = (values: PayWithCreditCardRequest) => {
        setCartInfo({
            ...cartInfo,
            newCreditCard: values,
        });
        if (!isEnabled(FeatureFlagTypes.enable_debit_web)) {
            cartInfoDetails.shipping.userAddresses.refreshAddresses();
        }
    };

    const onNewDebitCardConfirm = (values: PayWithDebitCardRequest) => {
        setCartInfo({
            ...cartInfo,
            newDebitCard: values,
        });
    };

    const setSelectedPayment = async (value: PaymentInfo) => {
        let paymentMethod = getPaymentMethod(value);

        if (paymentMethod && cart && homeDetails) {
            const { firstName, lastName } = homeDetails.profile;

            const name = `${firstName} ${lastName}`.trim() || '';

            if (value.id === 'new-credit-card') {
                setCartInfo({
                    ...cartInfo,
                    newDebitCard: { ...emptyDebitCard, name },
                    paymentMethodId: {
                        id: 'new-credit-card',
                        paymentType: PaymentType.CreditCard,
                        paymentSubType: PaymentSubType.Credit,
                    },
                });
                paymentMethod = {
                    paymentType: PaymentType.CreditCard,
                    paymentSubType: PaymentSubType.Credit,
                    achAccountId: null,
                    creditCardId: null,
                };
            }

            if (value.id === 'new-debit-card') {
                setCartInfo({
                    ...cartInfo,
                    newCreditCard: { ...emptyCreditCard, name },
                    paymentMethodId: {
                        id: 'new-debit-card',
                        paymentType: PaymentType.CreditCard,
                        paymentSubType: PaymentSubType.Debit,
                    },
                });
                paymentMethod = {
                    paymentType: PaymentType.CreditCard,
                    paymentSubType: PaymentSubType.Debit,
                    achAccountId: null,
                    creditCardId: null,
                };
            }

            if (['new-credit-card', 'new-debit-card'].includes(`${value.id}`)) {
                return;
            }

            setCartInfo({
                ...cartInfo,
                paymentMethodId: value,
            });
        }
    };

    const onBack = () => {
        history.push(Routes.ShopOrderSummary, cartInfo);
    };

    useEffect(() => {
        getDefaultPaymentMethodThunk()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        setCartInfo((currentCartInfo: CartInfoState) => ({
            ...currentCartInfo,
            setPaymentMethodAsDefault: makeThisTheDefaultPayment,
        }));
    }, [makeThisTheDefaultPayment, setCartInfo]);

    const setForceShipToCoordinator = async () => {
        await setCartInfo({
            ...cartInfo,
            shipToHome: false,
        });
        history.push(Routes.ShopOrderReview, cartInfo);
    };

    // FEC-2099
    useEffect(() => {
        if (isInIframe()) {
            window.parent.postMessage(
                {
                    type: 'view_checkout',
                    action: null,
                },
                mainWebsiteOrigin()
            );
        }
    }, []);

    const onContinue = async () => {
        let paymentType;
        if (paymentMethodId) {
            if (paymentMethodId.paymentType === PaymentType.Check) {
                paymentType = {
                    paymentType: PaymentType.Check,
                    achAccountId: null,
                    creditCardId: null,
                } as CheckPaymentMethod;
            } else if (paymentMethodId.paymentType === PaymentType.BankAccount) {
                paymentType = {
                    paymentType: PaymentType.BankAccount,
                    paymentSubType: null,
                    achAccountId: paymentMethodId.id as string,
                    creditCardId: null,
                } as ACHPaymentMethod;
            } else if (
                paymentMethodId.paymentType === PaymentType.CreditCard &&
                paymentMethodId.id !== 'new-debit-card' &&
                paymentMethodId.id !== 'new-credit-card'
            ) {
                paymentType = {
                    paymentType: PaymentType.CreditCard,
                    paymentSubType: paymentMethodId.paymentSubType,
                    creditCardId: paymentMethodId.id as number,
                    achAccountId: null,
                } as CreditCardPaymentMethod;
            } else if (
                paymentMethodId.id === 'new-credit-card' ||
                paymentMethodId.id === 'new-debit-card'
            ) {
                paymentType = {
                    paymentType: PaymentType.CreditCard,
                    paymentSubType: paymentMethodId.paymentSubType,
                    creditCardId: null,
                    achAccountId: null,
                } as PaymentMethod;
            }
            if (
                paymentType &&
                makeThisTheDefaultPayment &&
                paymentMethodId.id !== 'new-debit-card' &&
                paymentMethodId.id !== 'new-credit-card'
            ) {
                await setDefaultPaymentMethodThunk(paymentType)(dispatch);
            }
        }

        if (paymentType && cart) {
            const response = await addBillingInfo({
                cartId: cart.id,
                firstName: homeDetails!.profile.firstName,
                lastName: homeDetails!.profile.lastName,
                email: homeDetails!.profile.email,
                paymentMethod: paymentType,
            });
            if (response.error) {
                alert({ title: 'Error setting payment', message: SERVICE_UNAVAILABLE_MESSAGE });
            }
        }

        if (shipToHomeIfPossible) {
            if (cart && isOrderExceedsMaxCards(cart)) {
                setShowExceededModal(true);
                setCheckoutFlowState(CheckoutFlowState.DISABLED);
                return;
            }
            history.push(Routes.ShopOrderShipping, cartInfo);
        } else {
            history.push(Routes.ShopOrderReview, cartInfo);
        }
    };

    const demoFAQ = [
        { title: 'What if my bank account information changes?', content: 'Content 1', value: '1' },
        { title: 'Which credit cards xxxxx?', content: 'Content 2', value: '2' },
        { title: 'How do I xxxxx?', content: 'Content 3', value: '3' },
    ].map(({ title, content, value }) => ({
        value,
        title: <span className="font-semibold text-xs leading-1 text-grey-1">{title}</span>,
        content: (
            <div className="pl-12">
                <TextareaAutosize
                    className={styles.termsContent}
                    readOnly={true}
                    value={(content || '').toString()}
                />
            </div>
        ),
    }));

    const continueToShippingNoDebit = `Continue to ${
        !shipToHomeIfPossible ? 'Confirm Order' : 'Shipping'
    }`;

    const continueToShippingIsDebit = !shipToHomeIfPossible
        ? 'Review Order'
        : 'Continue to Shipping';

    const continueToShipping = isEnabled(FeatureFlagTypes.enable_debit_web)
        ? continueToShippingIsDebit
        : continueToShippingNoDebit;

    const continueToButtonUniqueClass = shipToHomeIfPossible
        ? 'cart_continue_to_shipping_button'
        : 'cart_review_order_button';

    const isCheckEnabled = Boolean(paymentTypes?.includes(PaymentType.Check));
    const orgSupportsPayingCoordinator = useRootSelector((rootState: RootState) => {
        return rootState?.store?.organization?.options?.isPayCoordinatorEnabled;
    });

    const continueToShippingDisabled =
        !cartInfo.paymentMethodId ||
        (cartInfo.paymentMethodId.paymentType === PaymentType.Check && !isCheckEnabled) ||
        (!orgSupportsPayingCoordinator &&
            cartInfo.paymentMethodId.paymentType === PaymentType.Check);

    const [hideButtons, setHideButtons] = useState(false);

    const renderCreditDebitCardForm = () => {
        if (paymentMethodId?.id === 'new-debit-card') {
            return (
                <CreditDebitCardForm
                    makeThisTheDefaultPayment={makeThisTheDefaultPayment}
                    setMakeThisTheDefaultPayment={setMakeThisTheDefaultPayment}
                    isDebitCard={true}
                />
            );
        }

        if (paymentMethodId?.id === 'new-credit-card') {
            return (
                <CreditDebitCardForm
                    makeThisTheDefaultPayment={makeThisTheDefaultPayment}
                    setMakeThisTheDefaultPayment={setMakeThisTheDefaultPayment}
                    isDebitCard={false}
                />
            );
        }

        return <></>;
    };

    let initialValues;
    let isNewDebitFormSelected = false;

    if (paymentMethodId?.id === 'new-credit-card') {
        initialValues = cartInfo.newCreditCard;
    } else if (paymentMethodId?.id === 'new-debit-card') {
        isNewDebitFormSelected = true;
        initialValues = cartInfo.newDebitCard;
    }

    const onSubmit = async (
        values: CreditCardFields,
        formik: FormikHelpers<PayWithCreditCardRequest>
    ) => {
        const cardNumber = values.cardNumber.replace(/\s/g, '');
        if (isNewDebitFormSelected && cardNumber) {
            const tokenExId = process.env.REACT_APP_TOKENEX_ID;
            const tokenExUrl = process.env.REACT_APP_TOKENEX_URL;
            const validation = await validateCardBinWithTokenX(cardNumber, tokenExId, tokenExUrl);

            if (validation.data) {
                const { isDebit } = validation?.data as any;

                if (!isDebit) {
                    formik.setFieldError('cardNumber', 'This number is linked to a Credit Card.');
                    return;
                }
            }
        }
        const payload = {
            ...values,
            cardNumber,
            expirationDate: values.expirationDate,
            address: { zipCode: values.zipCode.replace('/', '') },
            zipCode: values.zipCode.replace('/', ''),
        };

        if (isNewDebitFormSelected) {
            onNewDebitCardConfirm(payload as PayWithDebitCardRequest);
        } else {
            onNewCreditCardConfirm(payload as PayWithCreditCardRequest);
        }

        onContinue();
    };

    return (
        <div>
            <div className={styles.page}>
                <div className="w-full max-w-xl">
                    <Formik
                        key={paymentMethodId?.id}
                        validateOnMount={Boolean(initialValues)}
                        initialValues={
                            initialValues || {
                                ...emptyCreditCard,
                                name:
                                    `${homeDetails?.profile.firstName} ${homeDetails?.profile.lastName}`.trim() ||
                                    '',
                            }
                        }
                        isInitialValid={
                            !(
                                paymentMethodId?.id === 'new-credit-card' ||
                                paymentMethodId?.id === 'new-debit-card'
                            )
                        }
                        onSubmit={onSubmit}
                    >
                        {(formikProps) => {
                            return (
                                <>
                                    <div className="mb-14 pl-1">
                                        {!defaultPaymentAsyncState.loading && (
                                            <>
                                                <OrderPaymentOptionsWithDebit
                                                    shipToHome={Boolean(shipToHomeIfPossible)}
                                                    selectedPayment={cartInfo.paymentMethodId}
                                                    onPaymentChange={setSelectedPayment}
                                                    makeThisTheDefaultPayment={
                                                        makeThisTheDefaultPayment
                                                    }
                                                    setMakeThisTheDefaultPayment={
                                                        setMakeThisTheDefaultPayment
                                                    }
                                                >
                                                    {['new-credit-card', 'new-debit-card'].includes(
                                                        `${paymentMethodId?.id}`
                                                    )
                                                        ? renderCreditDebitCardForm()
                                                        : null}
                                                </OrderPaymentOptionsWithDebit>
                                            </>
                                        )}
                                        {defaultPaymentAsyncState.loading && <Skeleton />}
                                    </div>
                                    {!hideButtons && (
                                        <div className="flex md:block flex-col items-center">
                                            <Btn
                                                className={classNames(
                                                    `${continueToButtonUniqueClass} block md:hidden w-full mb-5`,
                                                    styles.btn
                                                )}
                                                disabled={
                                                    continueToShippingDisabled ||
                                                    !formikProps.isValid ||
                                                    formikProps.isValidating ||
                                                    disableCheckoutBtn
                                                }
                                                type="submit"
                                                onClick={() => formikProps.handleSubmit()}
                                            >
                                                {continueToShipping}
                                            </Btn>

                                            <Btn
                                                isSecondary={true}
                                                className={classNames(
                                                    'inline-block w-full md:w-auto mb-5',
                                                    styles.btn
                                                )}
                                                onClick={onBack}
                                                type="button"
                                            >
                                                Back
                                            </Btn>

                                            <Btn
                                                className={classNames(
                                                    `${continueToButtonUniqueClass} hidden md:inline-block ml-5 mb-5`,
                                                    styles.btn
                                                )}
                                                disabled={
                                                    continueToShippingDisabled ||
                                                    !formikProps.isValid ||
                                                    formikProps.isValidating ||
                                                    disableCheckoutBtn
                                                }
                                                type="submit"
                                                onClick={() => formikProps.handleSubmit()}
                                            >
                                                {continueToShipping}
                                            </Btn>
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    </Formik>

                    {/* hiding the Payment FAQ for now... Will be used in the future. */}
                    <div style={{ display: 'none' }}>
                        <div className="mt-20">
                            <div className="text-brand-dark font-bold text-xl">Payment FAQs</div>
                            <div className="border-t-2 border-brand mt-2">
                                <Accordion items={demoFAQ} />
                            </div>
                        </div>
                    </div>

                    <ModalExceedsMaxCards
                        maxCards={MAX_SHIP_TO_HOME_CARD_QUANTITY}
                        isOpen={showExceededModal}
                        closeModal={() => setShowExceededModal(false)}
                        isShipToCoordinatorEnabled={!!isShipCoordinatorEnabled}
                        primaryButton={
                            isShipCoordinatorEnabled
                                ? {
                                      text: 'Continue',
                                      action: () => {
                                          setForceShipToCoordinator();
                                          setShowExceededModal(false);
                                      },
                                  }
                                : { text: 'Ok', action: () => setShowExceededModal(false) }
                        }
                        secondaryButton={
                            isShipCoordinatorEnabled
                                ? {
                                      text: 'Close',
                                      action: () => setShowExceededModal(false),
                                  }
                                : undefined
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export const OrderPaymentScreen = () => {
    const className = isInIframe() ? 'pt-20' : 'pt-0';
    return (
        <OrderScreen
            title="Payment"
            orderStep="payment"
            className={className}
            showSummary={true}
            showMobileSummary={true}
            embedView={true}
        >
            <OrderPaymentScreenContent />
        </OrderScreen>
    );
};

export default OrderPaymentScreen;
